import { validate as isValidEmail } from 'email-validator';
import { getAttributionPayload } from '@src/services/posthog/attribution';

export const SIGNUP_VERSION = 'completion_v2';
export const MINIMUM_PASSWORD_LENGTH = 8;

// The configured Cognito pool uses case-insensitive usernames.
export function normalizeSignupEmail(email: string) {
  return email.trim().toLowerCase();
}

export function getSignupAttribution() {
  try {
    return getAttributionPayload();
  } catch {
    // Restricted or full browser storage must not prevent account creation.
    return {};
  }
}

export function validatePassword(password: string) {
  if (!password) return { field: 'password', reason: 'password_required', message: 'Create a password with at least 8 characters.' };
  if (password.length < MINIMUM_PASSWORD_LENGTH) return { field: 'password', reason: 'password_too_short', message: 'Use at least 8 characters for your password.' };
  return null;
}

export function validateSignup(email: string, password: string) {
  if (!email.trim()) return { field: 'email', reason: 'email_required', message: 'Enter your email address.' };
  if (!isValidEmail(email.trim())) return { field: 'email', reason: 'email_invalid', message: 'Enter a valid email address, such as name@example.com.' };
  return validatePassword(password);
}

// Use a bounded error taxonomy; never send a provider response, email, or password to analytics.
export function getSignupErrorCode(error: unknown): string {
  const allowed = [
    'UsernameExistsException', 'InvalidPasswordException', 'InvalidParameterException',
    'TooManyRequestsException', 'LimitExceededException', 'NetworkError',
    'UserNotConfirmedException', 'NotAuthorizedException', 'PasswordResetRequiredException',
    'UnexpectedLambdaException', 'UserLambdaValidationException', 'InvalidLambdaResponseException',
    'CodeMismatchException', 'ExpiredCodeException', 'UserNotFoundException',
  ];
  for (const value of [(error as any)?.code, (error as any)?.name, (error as any)?.__type]) {
    const code = typeof value === 'string' ? value.split('#').pop() : '';
    if (allowed.includes(code)) return code;
  }
  return 'UnknownError';
}

export function isRegistrationUncertain(error: unknown) {
  return ['UnknownError', 'NetworkError', 'UnexpectedLambdaException', 'UserLambdaValidationException', 'InvalidLambdaResponseException'].includes(getSignupErrorCode(error));
}

export function getPasswordErrorMessage(error: unknown) {
  // Only translate known password-policy reasons; never print raw provider text.
  const message = typeof (error as any)?.message === 'string' ? (error as any).message : '';
  if (/uppercase/i.test(message)) return 'Add an uppercase letter to your password and try again.';
  if (/lowercase/i.test(message)) return 'Add a lowercase letter to your password and try again.';
  if (/numeric|number/i.test(message)) return 'Add a number to your password and try again.';
  if (/symbol|special character/i.test(message)) return 'Add a symbol to your password and try again.';
  return 'This password was not accepted. Use at least 8 characters; if it is already that long, try a mix of uppercase and lowercase letters, numbers and symbols.';
}

export function getSignInErrorMessage(error: unknown) {
  switch (getSignupErrorCode(error)) {
    case 'UserNotConfirmedException': return 'Check your email to confirm your account, then sign in.';
    case 'PasswordResetRequiredException': return 'Reset your password before signing in.';
    case 'NotAuthorizedException':
    case 'UserNotFoundException': return 'The email or password was not accepted. Check your details or reset your password.';
    case 'TooManyRequestsException':
    case 'LimitExceededException': return 'Too many attempts. Wait a few minutes, then try again.';
    case 'NetworkError': return 'We could not connect. Check your internet connection and try again.';
    default: return 'We could not finish signing you in. Try again, or reset your password if needed.';
  }
}

export function getRecoveryErrorMessage(error: unknown) {
  switch (getSignupErrorCode(error)) {
    case 'InvalidPasswordException': return getPasswordErrorMessage(error);
    case 'CodeMismatchException': return 'That verification code is incorrect. Check the code in your email and try again.';
    case 'ExpiredCodeException': return 'That verification code has expired. Request a new code.';
    case 'TooManyRequestsException':
    case 'LimitExceededException': return 'Too many attempts. Wait a few minutes, then try again.';
    case 'NetworkError': return 'We could not connect. Check your internet connection and try again.';
    default: return 'We could not update your password. Try again or request a new verification code.';
  }
}

export function getSignupErrorMessage(error: unknown, accountCreated = false): string {
  const code = getSignupErrorCode(error);
  if (accountCreated) {
    if (code === 'UserNotConfirmedException') return 'Your account was created. Check your email to confirm it, then sign in.';
    if (code === 'NotAuthorizedException') return 'Your account was created, but we could not sign you in. Check your password or reset it below.';
    return 'Your account was created, but we could not finish signing you in. Try again to continue with this account.';
  }
  switch (code) {
    case 'UsernameExistsException': return 'An account with this email already exists. Sign in or reset your password below.';
    case 'InvalidPasswordException': return getPasswordErrorMessage(error);
    case 'InvalidParameterException': return 'Check your email address and password, then try again.';
    case 'TooManyRequestsException':
    case 'LimitExceededException': return 'Too many attempts. Wait a few minutes, then try again.';
    case 'NetworkError': return 'The connection was interrupted, so we could not confirm whether your account was created. Try signing in with the password you just used, or reset it below.';
    case 'UnexpectedLambdaException':
    case 'UserLambdaValidationException':
    case 'InvalidLambdaResponseException': return 'Our account setup service returned an error. Your account may already exist. Try signing in with the password you just used, or reset it below.';
    default: return 'We could not confirm whether your account was created. Try signing in with the password you just used, or reset it below. If neither works, contact support@lifeshack.com.';
  }
}
